import React, { useState, useEffect, useContext, useCallback } from "react";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import SubsectionContent from "./components/SubsectionContent";
import ModuleSidebar from "./components/ModuleSidebar";
import { UserContext } from "../../UserContext";
import ReflectionHistory from "./components/AIAgent/History/ReflectionHistory";
import LearnerContentFrame from "./components/LearnerContentFrame";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FaLock } from "react-icons/fa";
import { toast } from "react-hot-toast";
import ModuleDetailCard from "./components/LearningPath/ModuleDetailsCard";
import YourCareerProgressionJourney from "./components/LearningPath/YourCareerProgressionJourney";
import WelcomeIntroSection from "./components/LearningPath/WelcomeIntroSection";
import GuidesIntro from "./components/LearningPath/GuidesIntro";
import CareerProgressionJourney from "./components/LearningPath/CareerProgressionJourney";
import { apiFetch } from "../../services/apiFetch";
import { openRazorpayCheckout } from "../../utils/razorpayCheckout";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;
const DEFAULT_PREVIEW_UPGRADE_AMOUNT_PAISE = 399900;
const ACCOUNTABILITY_BOOKING_SOURCE = "accountability_booking";
const ACCOUNTABILITY_RETURN_STORAGE_KEY = "accountability_booking_return";
const PAYMENT_RETURN_PARAMS = [
  "source",
  "booking_id",
  "payment_intent_id",
  "course_id",
  "module_id",
  "module_slug",
  "subsection_id",
  "subsection_slug",
  "subsection_url",
  "razorpay_payment_id",
  "razorpay_payment_link_id",
  "razorpay_payment_link_reference_id",
  "razorpay_payment_link_status",
  "razorpay_signature",
];

const readStoredAccountabilityReturn = () => {
  try {
    return JSON.parse(
      sessionStorage.getItem(ACCOUNTABILITY_RETURN_STORAGE_KEY) || "null"
    );
  } catch {
    return null;
  }
};

const buildAccountabilityReturn = (params) => {
  if (params.get("source") !== ACCOUNTABILITY_BOOKING_SOURCE) return null;
  return PAYMENT_RETURN_PARAMS.reduce((acc, key) => {
    const value = params.get(key);
    if (value) acc[key] = value;
    return acc;
  }, {});
};

const formatCurrency = (amount, currency = "INR") => {
  if (!amount) return "--";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount / 100);
};

/**
 * Helper to check if completion modal has been shown
 */
const hasShownCompletionModal = (userId) => {
  if (!userId) return false;
  const key = `module1_completion_modal_shown_${userId}`;
  return localStorage.getItem(key) === "true";
};

/**
 * Helper to mark completion modal as shown
 */
const setCompletionModalShown = (userId) => {
  if (!userId) return;
  const key = `module1_completion_modal_shown_${userId}`;
  localStorage.setItem(key, "true");
};

const LearningPathPage = () => {
  const { layoutPhase, setLayoutPhase, resetKey } = useOutletContext();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseProgress, setCourseProgress] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [completedSubs, setCompletedSubs] = useState({});
  const [activeModuleIndex, setActiveModuleIndex] = useState(0);
  const [pendingModuleId, setPendingModuleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [showPreviewUpgradeModal, setShowPreviewUpgradeModal] = useState(false);
  const [showUpgradeNotice, setShowUpgradeNotice] = useState(false);
  const [hasModule1Completed, setHasModule1Completed] = useState(false);
  const [previewPaymentLoading, setPreviewPaymentLoading] = useState(false);
  const [previewPaymentError, setPreviewPaymentError] = useState("");
  const [previewUpgradeAmount, setPreviewUpgradeAmount] = useState(
    DEFAULT_PREVIEW_UPGRADE_AMOUNT_PAISE
  );
  const [previewUpgradeCurrency, setPreviewUpgradeCurrency] = useState("INR");
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const USER_ID = user?.user_id;
  const isPreviewUser = user?.preview_status === "preview";
  const [accountabilityReturn, setAccountabilityReturn] = useState(() =>
    readStoredAccountabilityReturn()
  );
  const [accountabilityReturnSelected, setAccountabilityReturnSelected] =
    useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentReturn = buildAccountabilityReturn(params);
    if (!paymentReturn) return;

    sessionStorage.setItem(
      ACCOUNTABILITY_RETURN_STORAGE_KEY,
      JSON.stringify(paymentReturn)
    );
    setAccountabilityReturn(paymentReturn);
    setAccountabilityReturnSelected(false);
    navigate(`${location.pathname}${location.hash || ""}`, { replace: true });
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!accountabilityReturn || accountabilityReturnSelected) return;

    const targetCourseId = accountabilityReturn.course_id;
    const targetModuleId = accountabilityReturn.module_id;
    const targetSubsectionId = accountabilityReturn.subsection_id;
    const targetSubsectionUrl = accountabilityReturn.subsection_url;

    if (
      !targetSubsectionId ||
      (targetSubsectionUrl && targetSubsectionUrl !== "call_from_partner")
    ) {
      return;
    }

    const findTargetCourse = () =>
      courses.find(
        (course) =>
          String(course.id || course.course_id) === String(targetCourseId) ||
          course.modules?.some((moduleItem) =>
            moduleItem.subsections?.some(
              (sub) => String(sub.id) === String(targetSubsectionId)
            )
          )
      );

    if (
      targetCourseId &&
      selectedCourse &&
      String(selectedCourse.id || selectedCourse.course_id) !==
        String(targetCourseId)
    ) {
      const targetCourse = findTargetCourse();
      if (targetCourse) {
        setSelectedCourse(targetCourse);
        setSelectedModule(null);
        setSelectedSubsection(null);
      }
      return;
    }

    if (!selectedCourse && courses.length) {
      const targetCourse = findTargetCourse();
      if (targetCourse) {
        setSelectedCourse(targetCourse);
      }
      return;
    }

    if (!courseProgress?.modules?.length) return;

    const moduleIndex = courseProgress.modules.findIndex(
      (moduleItem) =>
        (!targetModuleId || String(moduleItem.id) === String(targetModuleId)) &&
        moduleItem.subsections?.some(
          (sub) =>
            String(sub.id) === String(targetSubsectionId) &&
            (!targetSubsectionUrl || sub.subsection_url === targetSubsectionUrl)
        )
    );
    if (moduleIndex < 0) return;

    const moduleItem = courseProgress.modules[moduleIndex];
    const subsection = moduleItem.subsections?.find(
      (sub) =>
        String(sub.id) === String(targetSubsectionId) &&
        (!targetSubsectionUrl || sub.subsection_url === targetSubsectionUrl)
    );
    if (!subsection) return;

    setOnboardingScreen("complete");
    setActiveModuleIndex(moduleIndex);
    setSelectedModule(moduleItem);
    setSelectedSubsection(subsection);
    setLayoutPhase("intro");
    setAccountabilityReturnSelected(true);
  }, [
    accountabilityReturn,
    accountabilityReturnSelected,
    courseProgress,
    courses,
    selectedCourse,
    setLayoutPhase,
  ]);

  // ============ ONBOARDING STATE WITH VISIT COUNT ============
  const [onboardingScreen, setOnboardingScreen] = useState(null); // null = loading, "complete" = skip onboarding
  // const [visitCount, setVisitCount] = useState(0);

  // Check onboarding status and visit count on mount
  // useEffect(() => {
  //   if (!USER_ID) {
  //     setOnboardingScreen("complete");
  //     return;
  //   }

  //   const visitCountKey = `onboarding_visit_count_${USER_ID}`;
  //   const storedVisitCount = parseInt(localStorage.getItem(visitCountKey) || "0", 10);
    
  //   setVisitCount(storedVisitCount);

  //   // If user has visited less than 2 times, show onboarding
  //   if (storedVisitCount < 2) {
  //     setOnboardingScreen("welcome");
  //     // Increment visit count
  //     localStorage.setItem(visitCountKey, String(storedVisitCount + 1));
  //   } else {
  //     // 3rd visit onwards, skip onboarding
  //     setOnboardingScreen("complete");
  //   }
  // }, [USER_ID]);

  useEffect(() => {
    if (!USER_ID) {
      setOnboardingScreen("complete");
      return;
    }

    const visitCountKey = `onboarding_visit_count_${USER_ID}`;
    const storedVisitCount = parseInt(localStorage.getItem(visitCountKey) || "0", 10);

    // If user has visited less than 2 times, show onboarding
    if (storedVisitCount < 2) {
      setOnboardingScreen("welcome");
      // Increment visit count
      localStorage.setItem(visitCountKey, String(storedVisitCount + 1));
    } else {
      // 3rd visit onwards, skip onboarding
      setOnboardingScreen("complete");
    }
  }, [USER_ID]);

  useEffect(() => {
    const flag = sessionStorage.getItem("preview_upgrade_complete");
    if (flag === "true") {
      setShowUpgradeNotice(true);
      sessionStorage.removeItem("preview_upgrade_complete");
    }
  }, []);

  // Complete onboarding (called after user finishes journey screen)
  const completeOnboarding = () => {
    setOnboardingScreen("complete");
  };

  // Keep selected module/subsection references fresh as course progress updates
  useEffect(() => {
    if (!courseProgress?.modules?.length) return;

    setSelectedModule((prevModule) => {
      if (!prevModule?.id) return prevModule;
      return (
        courseProgress.modules.find((m) => m.id === prevModule.id) || prevModule
      );
    });

    setSelectedSubsection((prevSub) => {
      if (!prevSub?.id) return prevSub;
      const owningModule = courseProgress.modules.find((m) =>
        m.subsections?.some((s) => s.id === prevSub.id)
      );
      return (
        owningModule?.subsections?.find((s) => s.id === prevSub.id) || prevSub
      );
    });
  }, [courseProgress]);

  /* ---------------- Fetch all courses ---------------- */
  useEffect(() => {
    apiFetch(`${apiBaseURL}/courses`)
      .then((res) => res.json())
      .then((data) => (Array.isArray(data) ? setCourses(data) : setCourses([])))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, []);

  /* ---------------- Load user state ---------------- */
  useEffect(() => {
    if (!USER_ID || !courses.length || selectedCourse) return;

    apiFetch(`${apiBaseURL}/users/${USER_ID}/state`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data?.current_subsection) return;

        const foundCourse = courses.find((c) =>
          c.modules.some((m) =>
            m.subsections.some((s) => s.id === data.current_subsection)
          )
        );
        if (foundCourse) {
          setSelectedCourse(foundCourse);
        }
      })
      .catch(() => {});
  }, [courses, USER_ID, selectedCourse]);

  /* ---------------- Default course for first-time users ---------------- */
  useEffect(() => {
    if (!courses.length || selectedCourse) return;
    if (courses.length === 1) {
      setSelectedCourse(courses[0]);
    }
  }, [courses, selectedCourse]);

  const refreshCourseProgress = useCallback(() => {
    if (!selectedCourse || !USER_ID) return;
    apiFetch(
      `${apiBaseURL}/users/${USER_ID}/courses/${selectedCourse.id}/progress`
    )
      .then((res) => res.json())
      .then((data) => {
        setCourseProgress(data);
        const map = {};
        data.modules.forEach((m) =>
          m.subsections.forEach((s) => {
            if (s.completed || s.status === "completed") map[s.id] = true;
          })
        );
        setCompletedSubs(map);
      })
      .catch(() => setCourseProgress(null));
  }, [selectedCourse, USER_ID]);

  /* ---------------- Load course progress ---------------- */
  useEffect(() => {
    refreshCourseProgress();
  }, [refreshCourseProgress]);

  const isSubCompleted = useCallback(
    (sub) =>
      sub?.completed === true ||
      sub?.status === "completed" ||
      completedSubs?.[sub?.id] === true,
    [completedSubs]
  );

  const notifyIncompleteChat = useCallback((sub = selectedSubsection) => {
    if (sub?.type !== "ai" || isSubCompleted(sub)) return;
    toast.error("Chat is still in progress. Mark it complete when you're done.");
  }, [isSubCompleted, selectedSubsection]);

  const isModuleCompleted = useCallback(
    (moduleItem) =>
      moduleItem?.subsections?.length
        ? moduleItem.subsections.every((sub) => isSubCompleted(sub))
        : false,
    [isSubCompleted]
  );

  const isModuleUnlocked = useCallback(
    (moduleItem, index, modules = []) => {
      if (!moduleItem) return false;
      if (typeof moduleItem.is_unlocked === "boolean") {
        return moduleItem.is_unlocked;
      }
      if (isPreviewUser && moduleItem.order > 1) return false;
      if (index <= 0) return true;
      return modules
        .slice(0, index)
        .every((mod) => isModuleCompleted(mod));
    },
    [isModuleCompleted, isPreviewUser]
  );

  const getLastUnlockedIndex = useCallback(
    (modules) => {
      if (!modules?.length) return 0;
      let last = 0;
      modules.forEach((mod, idx) => {
        if (isModuleUnlocked(mod, idx, modules)) {
          last = idx;
        }
      });
      return last;
    },
    [isModuleUnlocked]
  );

  useEffect(() => {
    if (!courseProgress?.modules?.length) return;
    const modules = courseProgress.modules;
    const lastUnlockedIndex = getLastUnlockedIndex(modules);
    const resolveIndex = (idx) =>
      idx >= 0 && isModuleUnlocked(modules[idx], idx, modules)
        ? idx
        : lastUnlockedIndex;

    if (pendingModuleId) {
      const pendingIdx = modules.findIndex((m) => m.id === pendingModuleId);
      setActiveModuleIndex(resolveIndex(pendingIdx));
      setPendingModuleId(null);
      return;
    }

    const idx = modules.findIndex(
      (m) => m.id === courseProgress.active_module_id
    );
    setActiveModuleIndex(resolveIndex(idx));
  }, [courseProgress, pendingModuleId, isModuleUnlocked, getLastUnlockedIndex]);

  useEffect(() => {
    if (!selectedSubsection) return;
    setLayoutPhase("intro");
  }, [selectedSubsection, setLayoutPhase]);

  /* ---------------- Reset state when Learning Path button is clicked ---------------- */
  const prevResetKeyRef = React.useRef(resetKey);
  useEffect(() => {
    // When resetKey changes (user clicked Learning Path in sidebar), reset to main view
    if (resetKey !== prevResetKeyRef.current) {
      prevResetKeyRef.current = resetKey;
      setSelectedModule(null);
      setSelectedSubsection(null);
      setLayoutPhase("immersive");
    }
  }, [resetKey, setLayoutPhase]);

  /* ---------------- Check Module 1 Completion ---------------- */
  useEffect(() => {
    if (!courseProgress?.modules?.length || !USER_ID) return;

    const module1 = courseProgress.modules.find((m) => m.order === 1);
    if (!module1 || !module1.subsections?.length) return;

    const allCompleted = module1.subsections.every(
      (sub) => sub.completed === true || sub.status === "completed"
    );

    setHasModule1Completed(allCompleted);

    if (!allCompleted) {
      setShowCompletionModal(false);
      return;
    }

    if (isPreviewUser) {
      setShowPreviewUpgradeModal(true);
      return;
    }

    if (!hasShownCompletionModal(USER_ID)) {
      setShowCompletionModal(true);
      setCompletionModalShown(USER_ID);
    }
  }, [courseProgress, USER_ID, isPreviewUser]);

  const resolveCourseId = useCallback(() => {
    return (
      selectedCourse?.id ||
      selectedCourse?.course_id ||
      courseProgress?.course_id ||
      courseProgress?.course?.id ||
      courseProgress?.courseId ||
      courseProgress?.id ||
      (courses.length === 1 ? courses[0].id : null)
    );
  }, [
    selectedCourse?.id,
    selectedCourse?.course_id,
    courseProgress?.course_id,
    courseProgress?.course?.id,
    courseProgress?.courseId,
    courseProgress?.id,
    courses,
  ]);

  useEffect(() => {
    let isCancelled = false;

    const loadPaymentSettings = async () => {
      try {
        const res = await apiFetch(`${apiBaseURL}/payments/razorpay/settings`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok || isCancelled) return;

        const configuredAmount = Number(data.subscription_amount);
        if (Number.isFinite(configuredAmount) && configuredAmount > 0) {
          setPreviewUpgradeAmount(configuredAmount);
        }
        if (data.currency) {
          setPreviewUpgradeCurrency(data.currency);
        }
      } catch (error) {
        console.error("Error loading payment settings:", error);
      }
    };

    loadPaymentSettings();

    return () => {
      isCancelled = true;
    };
  }, []);

  const previewPriceDisplay = formatCurrency(
    previewUpgradeAmount,
    previewUpgradeCurrency
  );
  const previewUpgradeTitle = hasModule1Completed
    ? "Congratulations! Module 1 Complete"
    : "Unlock the Full Course";
  const previewUpgradeBody = hasModule1Completed
    ? "You’re in preview mode. Upgrade to unlock the full course."
    : "You’re in preview mode. Upgrade anytime to unlock the full course.";

  const resolvePreviewPhone = useCallback(() => {
    const raw =
      user?.phone || user?.phone_number || user?.mobile || user?.mobile_number;
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 14) return "";
    return digits;
  }, [user?.phone, user?.phone_number, user?.mobile, user?.mobile_number]);

  useEffect(() => {
    if (!showPreviewUpgradeModal) return;
    setPreviewPaymentError("");
  }, [showPreviewUpgradeModal]);

  const createPreviewPaymentLink = useCallback(async (phoneOverride = "") => {
    const courseId = resolveCourseId();
    if (!USER_ID || !courseId) {
      setPreviewPaymentError("Missing course information for payment.");
      return "";
    }

    setPreviewPaymentLoading(true);
    setPreviewPaymentError("");
    try {
      const websiteUrl =
        process.env.REACT_APP_WEBSITE_URL || window.location.origin;
      const phone = phoneOverride || resolvePreviewPhone();
      const payload = {
        user_id: USER_ID,
        course_id: courseId,
        description: "Preview upgrade",
        callback_url: `${websiteUrl}/signUp?auth=login&preview_upgrade=1`,
        notes: { source: "preview" },
      };
      if (phone) {
        payload.phone = phone;
      }

      const res = await apiFetch(
        `${apiBaseURL}/payments/razorpay/preview-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Failed to create payment link.");
      }

      return data;
    } catch (error) {
      console.error("Error creating preview payment link:", error);
      setPreviewPaymentError(
        error?.message || "Failed to create payment link."
      );
      return "";
    } finally {
      setPreviewPaymentLoading(false);
    }
  }, [
    USER_ID,
    resolveCourseId,
    resolvePreviewPhone,
  ]);

  const handlePreviewUpgradeClick = useCallback(async () => {
    if (previewPaymentLoading) return;
    const order = await createPreviewPaymentLink();
    if (!order?.order_id) return;
    try {
      await openRazorpayCheckout({
        order,
        verify: async (result) => {
          const res = await apiFetch(`${apiBaseURL}/payments/razorpay/order/verify`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ order_id: result.razorpay_order_id, payment_id: result.razorpay_payment_id, signature: result.razorpay_signature }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.detail || "Payment verification failed.");
          return data;
        },
      });
      toast.success("Payment verified. Full course access is now unlocked.");
      window.location.reload();
    } catch (error) {
      setPreviewPaymentError(error?.message || "Payment was not completed.");
    }
  }, [previewPaymentLoading, createPreviewPaymentLink]);

  /* ---------------- Handlers ---------------- */
  const handleProgressUpdate = useCallback((update) => {
    if (!update) return;

    if (update?.modules?.length) {
      setCourseProgress(update);
      const map = {};
      update.modules.forEach((m) =>
        m.subsections.forEach((s) => {
          if (s.completed || s.status === "completed") map[s.id] = true;
        })
      );
      setCompletedSubs(map);
      return;
    }

    const updatedSubId =
      update.subsection_id ||
      update.subsectionId ||
      update.subsection?.id ||
      update.id;
    const updatedStatus = update.status;
    const updatedCompleted = update.completed === true;

    if (!updatedSubId) return;

    setCompletedSubs((prev) => ({
      ...prev,
      [updatedSubId]:
        typeof updatedStatus === "string"
          ? updatedStatus === "completed" || prev[updatedSubId]
          : updatedCompleted || prev[updatedSubId],
    }));

    setCourseProgress((prev) => {
      if (!prev?.modules?.length) return prev;
      return {
        ...prev,
        modules: prev.modules.map((m) => ({
          ...m,
          subsections: m.subsections?.map((s) => {
            if (s.id !== updatedSubId) return s;
            const nextCompleted =
              typeof updatedStatus === "string"
                ? updatedStatus === "completed" || s.completed
                : updatedCompleted || s.completed;
            return {
              ...s,
              status: nextCompleted ? "completed" : updatedStatus || s.status,
              completed: nextCompleted,
            };
          }),
        })),
      };
    });
  }, []);

  const handleSubsectionComplete = () => {
    if (!selectedCourse || !selectedModule || !selectedSubsection) return;

    const modules = selectedCourse.modules;
    const currentModule = selectedModule;
    const currentSub = selectedSubsection;
    setCompletedSubs((prev) => ({
      ...prev,
      [currentSub.id]: true,
    }));

    setCourseProgress((prev) => {
      if (!prev?.modules?.length) return prev;
      return {
        ...prev,
        modules: prev.modules.map((m) => ({
          ...m,
          subsections: m.subsections?.map((s) => {
            if (s.id !== currentSub.id) return s;
            return {
              ...s,
              status: "completed",
              completed: true,
            };
          }),
        })),
      };
    });

    // 1️⃣ Find index of current module
    const moduleIdx = modules.findIndex((m) => m.id === currentModule.id);
    if (moduleIdx === -1) return;

    // 2️⃣ Find index of current subsection
    const subs = currentModule.subsections;
    const subIdx = subs.findIndex((s) => s.id === currentSub.id);
    if (subIdx === -1) return;

    // 3️⃣ If next subsection in same module → go there
    const nextSub = subs[subIdx + 1];
    if (nextSub) {
      setSelectedSubsection(nextSub);
      return;
    }

    // 4️⃣ Otherwise move to next module
    const nextModule = modules[moduleIdx + 1];
    if (nextModule) {
      setSelectedModule(null);
      setSelectedSubsection(null);
      setLayoutPhase("immersive");
      refreshCourseProgress();
      return;
    }

    // 5️⃣ No next content → course finished
    navigate("/learner/action-plan");
  };

  const renderSideModule = (moduleItem, realIndex, modules) => {
    const isCompleted = isModuleCompleted(moduleItem);
    const isUnlocked = isModuleUnlocked(moduleItem, realIndex, modules);
    const nextModule = modules[realIndex + 1];
    const nextModuleUnlocked = nextModule
      ? isModuleUnlocked(nextModule, realIndex + 1, modules)
      : false;
    const showCompletedColor = isUnlocked && (nextModuleUnlocked || isCompleted);

    return (
      <div
        key={moduleItem.id}
        onClick={() => isUnlocked && setActiveModuleIndex(realIndex)}
        title={
          isUnlocked
            ? ""
            : "Complete previous modules to unlock this module."
        }
        className={`w-[72px] h-[420px] rounded-2xl 
          flex items-center justify-center
          transition-all duration-300
          ${showCompletedColor ? "bg-[#6FB1AF]" : "bg-[#D1D5DB]"}
          ${isUnlocked ? "cursor-pointer" : "cursor-not-allowed opacity-70"}
          hover:scale-[1.03]
          relative
        `}
      >
        <div className="flex h-full w-full flex-col items-center justify-center px-2 pb-6 pt-8">
          <span
            className="text-white text-base font-normal text-center leading-tight"
            style={{
              writingMode: "vertical-rl",
              transform: "rotate(180deg)",
            }}
          >
            {moduleItem.title}
          </span>
          <span className="absolute bottom-4 text-white text-base font-normal">
            {moduleItem.order}
          </span>
        </div>
        {!isUnlocked && (
          <FaLock className="absolute top-[12px] right-[12px] text-white/80" />
        )}
      </div>
    );
  };

  /* ---------------- Layout Flags ---------------- */
  const isImmersive = layoutPhase === "immersive";
  const isFull = layoutPhase === "full";

  /* ---------------- Legacy Adapter for Immersive ---------------- */
  // Some old components still call `onToggleImmersive(true|false)`
  const handleLegacyImmersiveToggle = (flagOrPhase) => {
    if (typeof flagOrPhase === "boolean") {
      setLayoutPhase(flagOrPhase ? "immersive" : "intro");
    } else if (typeof flagOrPhase === "string") {
      setLayoutPhase(flagOrPhase);
    }
  };

  const handleWidgetPhaseChange = useCallback(
    (phase) => {
      setLayoutPhase(phase);
    },
    [setLayoutPhase]
  );

  /* ============ MODAL HANDLERS ============ */
  const handleCloseCompletionModal = () => {
    if (isPreviewUser) return;
    setShowCompletionModal(false);
  };

  const handleClosePreviewUpgradeModal = () => {
    setShowPreviewUpgradeModal(false);
  };

  const handleGoToMySpace = () => {
    setShowCompletionModal(false);
    navigate("/learner");
  };

  /* ============ ONBOARDING SCREENS ============ */
  const renderOnboardingContent = () => {
    if (onboardingScreen === "welcome") {
      return (
        <div className="animate-fadeIn">
          <WelcomeIntroSection onNext={() => setOnboardingScreen("guides")} />
        </div>
      );
    }

    if (onboardingScreen === "guides") {
      return (
        <div className="animate-fadeIn">
          <GuidesIntro onNext={() => setOnboardingScreen("journey")} />
        </div>
      );
    }

    if (onboardingScreen === "journey") {
      return (
        <div className="animate-fadeIn">
          <CareerProgressionJourney onComplete={completeOnboarding} />
        </div>
      );
    }

    return null;
  };

  /* ============ MAIN CONTENT ============ */
  const handleFocusLearningPath = useCallback(() => {
    const section = document.getElementById("learningPath");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const renderMainContent = () => {
    const modules = courseProgress?.modules || [];
    const activeModule = modules[activeModuleIndex];
    const activeModuleUnlocked =
      modules.length > 0 &&
      isModuleUnlocked(activeModule, activeModuleIndex, modules);
    const nextIndex = activeModuleIndex + 1;
    const canGoPrev = activeModuleIndex > 0;
    const canGoNext =
      nextIndex < modules.length &&
      isModuleUnlocked(modules[nextIndex], nextIndex, modules);

    return (
      <div className="h-auto bg-white rounded-2xl">
      {!selectedSubsection && (
        <YourCareerProgressionJourney
          onFocusLearningPath={handleFocusLearningPath}
        />
      )}

      <div className="mt-0" id="learningPath">
        {/* ---------------- Course List ---------------- */}
        {!selectedCourse && (
          <div className="p-10 h-full overflow-y-auto">
            <h1 className="text-2xl font-bold mb-6 text-ink">
              Available Courses
            </h1>

            {loading && <p className="text-muted">Loading courses…</p>}
            {!loading && courses.length === 0 && (
              <p className="text-muted">No courses available.</p>
            )}

            {courses.map((course) => (
              <div
                key={course.id}
                className="p-5 border border-gray-200 rounded-xl mb-4 cursor-pointer hover:bg-gray-50 transition"
                onClick={() => {
                  setSelectedCourse(course);
                  setSelectedModule(null);
                  setSelectedSubsection(null);
                }}
              >
                <h2 className="text-lg font-semibold text-ink">
                  {course.title}
                </h2>
                <p className="text-sm text-muted">{course.description}</p>
              </div>
            ))}
          </div>
        )}

        {/* ---------------- Module View ---------------- */}
        {selectedCourse && !selectedSubsection && (
          <div className="p-10 h-auto overflow-y-auto">
            {/* <h1 className="text-2xl font-bold mb-6 md:mb-4 text-ink font-gotham">
              {selectedCourse.title}
            </h1> */}

            {courseProgress?.modules?.length ? (
              <div className="mt-8 md:mt-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-medium font-gotham">
                    Your Learning Path
                  </h2>

                  <div className="flex gap-3">
                    <button
                      onClick={() =>
                        canGoPrev &&
                        setActiveModuleIndex((i) => Math.max(i - 1, 0))
                      }
                      disabled={!canGoPrev}
                      className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center disabled:opacity-40"
                    >
                      <ChevronLeft size={16} />
                    </button>

                    <button
                      onClick={() => canGoNext && setActiveModuleIndex(nextIndex)}
                      disabled={!canGoNext}
                      className="w-9 h-9 rounded-full bg-black text-white flex items-center justify-center disabled:opacity-40"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-6">
                  <div className="flex gap-4">
                    {courseProgress.modules
                      .slice(0, activeModuleIndex)
                      .map((moduleItem, index) =>
                        renderSideModule(moduleItem, index, modules)
                      )}
                  </div>

                  <ModuleDetailCard
                    module={activeModule}
                    completedSubs={completedSubs}
                    isUnlocked={activeModuleUnlocked}
                    onSelectSubsection={(sub) => {
                      setSelectedModule(
                        courseProgress.modules[activeModuleIndex]
                      );
                      setSelectedSubsection(sub);
                    }}
                  />

                  <div className="flex gap-4">
                    {courseProgress.modules
                      .slice(activeModuleIndex + 1)
                      .map((moduleItem, index) =>
                        renderSideModule(
                          moduleItem,
                          activeModuleIndex + index + 1,
                          modules
                        )
                      )}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-muted">No modules available.</p>
            )}
          </div>
        )}

        {/* ---------------- Subsection View ---------------- */}
        {selectedCourse && selectedSubsection && (
          <div className="h-auto p-6">
            <SubsectionContent
              subsection={selectedSubsection}
              userId={USER_ID}
              completed={completedSubs[selectedSubsection.id]}
              onProgressUpdate={handleProgressUpdate}
              onComplete={handleSubsectionComplete}
              selectedCourse={selectedCourse}
              selectedModule={selectedModule}
              onToggleImmersive={handleLegacyImmersiveToggle}
              onWidgetPhaseChange={handleWidgetPhaseChange}
              onGoToCourse={() => {
                notifyIncompleteChat();
                setSelectedSubsection(null);
                setSelectedModule(null);
                setLayoutPhase("immersive");
                refreshCourseProgress();
              }}
              onGoToModule={() => {
                notifyIncompleteChat();
                if (selectedModule?.id) {
                  const currentIdx = courseProgress?.modules?.findIndex(
                    (m) => m.id === selectedModule.id
                  );
                  if (currentIdx >= 0) {
                    setActiveModuleIndex(currentIdx);
                  }
                }
                setLayoutPhase("intro");
                refreshCourseProgress();
              }}
              onSelectModule={setSelectedModule}
              onSelectSubsection={(sub) => {
                if (sub?.id !== selectedSubsection?.id) {
                  notifyIncompleteChat();
                }
                setSelectedSubsection(sub);
              }}
            />
          </div>
        )}
      </div>
    </div>
    );
  };

  /* ---------------- Render ---------------- */
  // Show loading state while checking onboarding
  if (onboardingScreen === null) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
          <p className="text-gray-500 text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ---------- Main Content Area ---------- */}
      <LearnerContentFrame layoutPhase={layoutPhase}>
        {!isPreviewUser && showUpgradeNotice && (
          <div className="mx-6 mt-6 mb-2 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#E9F2EC] bg-[#F2FBF6] px-4 py-3 text-sm text-[#1E5A3D]">
            <div>
              <span className="font-semibold">Upgraded to full access.</span>{" "}
              You now have access to all modules.
            </div>
            <button
              onClick={() => setShowUpgradeNotice(false)}
              className="rounded-lg bg-[#2F855A] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#276F4B]"
            >
              Dismiss
            </button>
          </div>
        )}
        {isPreviewUser && (
          <div className="mx-6 mt-6 mb-2 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#E6F3F3] bg-[#F3FAFA] px-4 py-3 text-sm text-[#1F5B5A]">
            <div>
              <span className="font-semibold">Preview mode:</span>{" "}
              Upgrade anytime to unlock all modules.
            </div>
            <button
              onClick={() => setShowPreviewUpgradeModal(true)}
              className="rounded-lg bg-[#54B0AF] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#4a9998]"
            >
              Upgrade now
            </button>
          </div>
        )}
        {/* Show onboarding OR main content */}
        {onboardingScreen === "complete"
          ? renderMainContent()
          : renderOnboardingContent()}
      </LearnerContentFrame>

      {/* ---------- Right Sidebar ---------- */}
      {!isImmersive && !isFull && selectedCourse && selectedSubsection && (
        <div className="absolute right-[32px] top-[25px] w-[356px] flex flex-col gap-4 z-40">
          <div className="max-h-[calc(100vh-8rem)] overflow-y-auto">
            <ModuleSidebar
              course={selectedCourse}
              module={selectedModule}
              selectedSubsection={selectedSubsection}
              completedSubs={completedSubs}
              isPreviewUser={isPreviewUser}
              onSelectModule={setSelectedModule}
              onSelectSubsection={setSelectedSubsection}
              onReturnToLearning={(nextModule) => {
                if (nextModule?.id) {
                  const modules = courseProgress?.modules || [];
                  const nextIdx = modules.findIndex((m) => m.id === nextModule.id);
                  if (nextIdx >= 0) {
                    setActiveModuleIndex(nextIdx);
                    setPendingModuleId(nextModule.id);
                  }
                }
                setSelectedSubsection(null);
                setSelectedModule(null);
                setLayoutPhase("immersive");
                refreshCourseProgress();
              }}
              onGoToActionPlan={() => {
                if (hasModule1Completed) {
                  navigate("/learner/action-plan");
                }
              }}
            />
          </div>

          {selectedSubsection?.subsection_url ===
            "general_reflection_agent" && <ReflectionHistory />}
        </div>
      )}

      {/* Preview Upgrade Modal */}
      {showPreviewUpgradeModal && isPreviewUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-end">
              <button
                onClick={handleClosePreviewUpgradeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="text-center mt-2">
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto bg-[#54B0AF] bg-opacity-10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-[#54B0AF]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-medium font-gotham text-[#1D1D1D] mb-2">
                {previewUpgradeTitle}
              </h2>

              <p className="text-base text-gray-600 font-gotham mb-4">
                {previewUpgradeBody}
              </p>

              <div className="rounded-lg border border-[#E6F3F3] bg-[#F6FBFB] px-4 py-3 text-sm text-[#1F5B5A] mb-5">
                <span className="font-semibold">Course price:</span>{" "}
                <span className="line-through text-[#8FB6B5] mr-2">₹19,999</span>
                <span className="font-semibold">{previewPriceDisplay}</span>
              </div>

                <div className="flex flex-col gap-3">
                  <button
                    onClick={handlePreviewUpgradeClick}
                    disabled={previewPaymentLoading}
                    className="w-full bg-[#54B0AF] text-white py-3 px-6 rounded-lg font-medium font-gotham hover:bg-[#4a9998] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                  {previewPaymentLoading
                    ? "Preparing payment link..."
                    : "Upgrade to Continue"}
                </button>

                {previewPaymentError && (
                  <p className="text-sm text-red-600">
                    {previewPaymentError}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Module 1 Completion Modal */}
      {showCompletionModal && !isPreviewUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-end">
              <button
                onClick={handleCloseCompletionModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="text-center mt-4">
              <div className="mb-4">
                <div className="w-16 h-16 mx-auto bg-[#54B0AF] bg-opacity-10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-[#54B0AF]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>

              <h2 className="text-2xl font-medium font-gotham text-[#1D1D1D] mb-3">
                Congratulations! Module 1 Complete
              </h2>

              <p className="text-base text-gray-600 font-gotham mb-6">
                You've successfully completed Module 1! Visit My Space to view your personalized learning summary and track your progress.
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleGoToMySpace}
                  className="w-full bg-black text-white py-3 px-6 rounded-lg font-medium font-gotham hover:bg-gray-800 transition-colors"
                >
                  Go to My Space
                </button>

                <button
                  onClick={handleCloseCompletionModal}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium font-gotham hover:bg-gray-200 transition-colors"
                >
                  Stay on the Learning Path
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LearningPathPage;
